import { __decorate } from "tslib";
import { Group, MathUtils, Matrix4 } from "three";
import { Config } from "../../Config/Config.js";
import { Save, SaveChild } from "../../Core/Save.js";
import { ViewType } from "../../Models/AppModel.js";
import { bindedModelBase } from "../../Models/BindedModel.js";
import { AnatomicalSideEnum, BoneTypeEnum, PlanTypeEnum, ReferenceTypeEnum } from "../../Models/CaseModel.js";
import { CachedHTMLImages, Consts } from "../../Utils/Consts.js";
import { Default } from "../../Utils/Default.js";
import { LimitUtils } from "../../Utils/LimitUtils.js";
import { Utils } from "../../Utils/Utils.js";
import { VectorUtils } from "../../Utils/VectorUtils.js";
import { DraggablePoint } from "../Primitive/DraggablePoint.js";
import { Label } from "../Primitive/Label/Label.js";
import { LabelAngle } from "../Primitive/Label/LabelAngle.js";
import { LimitLine } from "../Primitive/LimitLine.js";
import { LinePointToPoint2 } from "../Primitive/LinePointToPoint2.js";
import { ConvexityPoint } from "./ConvexityPoint.js";
export var AnatomicalAxisType;
(function (AnatomicalAxisType) {
    AnatomicalAxisType[AnatomicalAxisType["reference"] = 0] = "reference";
    AnatomicalAxisType[AnatomicalAxisType["moving"] = 1] = "moving";
})(AnatomicalAxisType || (AnatomicalAxisType = {}));
// TODO: Ac e Bc non devono allinearsi
export class AnatomicalAxis extends Group {
    get oppositePoint() { return this._params.type === PlanTypeEnum.Deformity ? this.Bc.position : this.center.position; }
    get middlePointB1B2() { return this._params.type === PlanTypeEnum.Deformity ? this.Bc : this.B; }
    get oppositeToAc() { return this.B; }
    get _currentConvexityPoint() {
        for (const point of this._convexityPoints) {
            if (point.convexityValue === this._selectedConvexity) {
                return point;
            }
        }
        return null;
    }
    get selectedConvexity() {
        return this._selectedConvexity;
    }
    set selectedConvexity(value) {
        const newPoint = this._convexityPoints[this._params.convexityList.AvailableConvexity.indexOf(value)];
        this._currentConvexityPoint?.unselect();
        this._selectedConvexity = newPoint.select();
    }
    get AB() {
        return this.A.position.clone().sub(this.B.position);
    }
    get axisApexA() {
        return this.oppositeToAc.position;
    }
    get axisApexB() {
        return this.Ac.position;
    }
    onSelect() {
        Default.colorAllDraggablePoints(this);
    }
    onDeselect() {
        Default.restoreColorAllDraggablePoints(this);
    }
    onAfterRestore() {
        this._convexityPoints && (this.selectedConvexity = this._selectedConvexity);
    }
    constructor(params, isEoC = false) {
        super();
        this.isSelectable = true;
        this.isDeletable = false;
        this.isSelected = false;
        this.minDistance_A_Ac = 10;
        this.minDistance_A1_A2 = 6; //todo use this properties
        this.minDistance_B1_B2 = 6;
        this.minDistance_Ac_Bc = 6;
        this.minDistance_Bc_oppositeBc = 5;
        this._defaultHeightDeformity = 80;
        this._defaultHeightFracture = 70;
        this._defaultDistanceAcToA = 10;
        this._defaultTreesDistanceDeformity = 10;
        this._defaultTreesDistanceFracture = 40;
        this._limitLines = [];
        this._enableLimits = false; // refresh page to make it works (to improve performance when don't use it)
        this._angleLocked = false;
        this.toggleLockAngle = (test, history = true) => {
            history && this.parent.dispatchEvent({ type: "startTracking" });
            this._angleLocked = !this._angleLocked;
            this.leftLabel.setValidParam("imageBottomRight", this._angleLocked ? CachedHTMLImages.lock : CachedHTMLImages.empty);
            this.rightLabel.setValidParam("imageBottomRight", this._angleLocked ? CachedHTMLImages.lock : CachedHTMLImages.empty);
            this.leftLabel.label.cursorOnHover = this.getAngleLockCursor();
            this.rightLabel.label.cursorOnHover = this.getAngleLockCursor();
            history && this.parent.dispatchEvent({ type: "detectChanges" });
        };
        this.name = params.axisType === AnatomicalAxisType.reference ? "Reference" : "Moving";
        this._params = params;
        const viewType = params.viewType;
        if (!isEoC && this._enableLimits) {
            this._limitLines[0] = new LimitLine(viewType, 0xff0000);
            this._limitLines[1] = new LimitLine(viewType, 0x00ff00);
            this._limitLines[2] = new LimitLine(viewType, 0x0000ff);
            this._limitLines[3] = new LimitLine(viewType, 0xffffff);
            this._limitLines[4] = new LimitLine(viewType, 0xff00ff);
        }
        const colorHex = params.axisType === AnatomicalAxisType.reference ? Config.reference_colorHex : Config.moving_colorHex;
        const colorCss = params.axisType === AnatomicalAxisType.reference ? Config.reference_colorCss : Config.moving_colorCss;
        const colorCssAlpha = params.axisType === AnatomicalAxisType.reference ? Config.reference_colorCssAlpha : Config.moving_colorCssAlpha;
        this.add(this.A = new DraggablePoint('A', viewType, colorHex), this.Ac = new DraggablePoint('Ac', viewType, colorHex), this.A1 = new DraggablePoint('A1', viewType, colorHex), this.A2 = new DraggablePoint('A2', viewType, colorHex), this.B = new DraggablePoint('B', viewType, colorHex), this.B1 = new DraggablePoint('B1', viewType, colorHex), this.B2 = new DraggablePoint('B2', viewType, colorHex), this.center = new DraggablePoint('center', viewType, colorHex), this.A_B_Line = new LinePointToPoint2('A_B_Line', viewType, colorHex, this.A, this.B), this.A1_A2_Line = new LinePointToPoint2('A1_A2_Line', viewType, colorHex, this.A1, this.A2), this.B1_B2_Line = new LinePointToPoint2('B1_B2_Line', viewType, colorHex, this.B1, this.B2));
        if (params.type == PlanTypeEnum.Deformity) {
            this.add(this.Bc = new DraggablePoint('Bc', viewType, colorHex));
        }
        if (!isEoC) {
            this.add(this.leftLabel = new LabelAngle({
                name: 'leftLabel',
                arcA: this.A1,
                arcB: this.B,
                arcCenter: this.Ac,
                viewType: viewType,
                placeOnBisector: true,
                labelParams: { backgroundColor: colorCssAlpha, fontColor: Config.moving_reference_fontColor, borderColor: colorCss }
            }), this.rightLabel = new LabelAngle({
                name: 'rightLabel',
                arcA: this.A2,
                arcB: this.B,
                arcCenter: this.Ac,
                viewType: viewType,
                placeOnBisector: true,
                labelParams: { backgroundColor: colorCssAlpha, fontColor: Config.moving_reference_fontColor, borderColor: colorCss },
            }), ...this._limitLines);
            this.toggleLockAngle(undefined, false);
            this.initConvexityPoints();
            this.bindEvents();
            this.initPosition(params.angle, params.translHor, params.translVer);
            this.selectedConvexity = params.convexityList.DefaultConvexity;
            this.add(this.nameLabel = new Label("nameLabel", viewType, this.A, this.AB_dirOrigin.clone().setLength(10), {
                backgroundColor: colorCssAlpha, fontColor: Config.moving_reference_fontColor, borderColor: colorCss,
                text: this.name
            }));
        }
    }
    initConvexityPoints() {
        this._convexityPoints = [];
        for (const convexity of this._params.convexityList.AvailableConvexity) {
            const point = new ConvexityPoint("convexityPoint", this._params.viewType, this.A1, this.A2, convexity, this._params.axisType);
            this._convexityPoints.push(point);
            point.bindEvent("onClick", () => {
                this.parent.dispatchEvent({ type: "startTracking" });
                this._currentConvexityPoint?.unselect();
                this._selectedConvexity = point.select();
                this.parent.dispatchEvent({ type: "detectChanges" });
            });
        }
        ;
        this.add(...this._convexityPoints);
    }
    initPosition(angle = 0, translHor = 0, translVer) {
        const treeHeigth = this._params.type === PlanTypeEnum.Deformity ? this._defaultHeightDeformity : this._defaultHeightFracture;
        const distanceBetweenTrees = this._params.type === PlanTypeEnum.Deformity ? this._defaultTreesDistanceDeformity : this._defaultTreesDistanceFracture;
        const distancesSegment = Utils.getAnatomicalAxisLengths(this._params.viewType, this._params.boneType, this._params.axisType, this._params.refType);
        const footRule = Utils.isFoot(this._params.boneType) && this._params.viewType == ViewType.LT;
        const ABsign = (this._params.axisType === AnatomicalAxisType.reference ? 1 : -1) * (this._params.refType === ReferenceTypeEnum.Proximal ? 1 : -1) *
            (footRule && this._params.side === AnatomicalSideEnum.Right ? -1 : 1);
        this.AB_dirOrigin = Consts.verDir.clone().multiplyScalar(ABsign);
        this.AB_dirOrigin_perp = Consts.horDir.clone();
        footRule && this.AB_dirOrigin.applyAxisAngle(Consts.planeNormal, Math.PI / 2);
        footRule && this.AB_dirOrigin_perp.applyAxisAngle(Consts.planeNormal, Math.PI / 2);
        const startPosition = this.AB_dirOrigin.clone().setLength(distanceBetweenTrees / 2);
        const dir = this.AB_dirOrigin.clone().applyAxisAngle(Consts.planeNormal, angle);
        const dirPerp = this.AB_dirOrigin_perp.clone().applyAxisAngle(Consts.planeNormal, angle);
        if (this._params.type === PlanTypeEnum.Fracture) {
            translVer && startPosition.setLength(translVer - distanceBetweenTrees / 2);
            startPosition.add(this.AB_dirOrigin_perp.clone().setLength(translHor));
        }
        this.A.position.addVectors(dir.clone().setLength(treeHeigth), startPosition);
        this.Ac.position.addVectors(dir.clone().setLength(treeHeigth - this._defaultDistanceAcToA), startPosition);
        this.A1.position.addVectors(dirPerp.clone().setLength(distancesSegment.A1A2 / -2), this.Ac.position);
        this.A2.position.addVectors(dirPerp.clone().setLength(distancesSegment.A1A2 / 2), this.Ac.position);
        this.B.position.copy(startPosition);
        this.Bc?.position.addVectors(dir.clone().setLength(this._defaultDistanceAcToA), startPosition);
        this.B1.position.addVectors(dirPerp.clone().setLength(distancesSegment.B1B2 / -2), this.middlePointB1B2.position);
        this.B2.position.addVectors(dirPerp.clone().setLength(distancesSegment.B1B2 / 2), this.middlePointB1B2.position);
        this.center.position.lerpVectors(this.Ac.position, this.middlePointB1B2.position, 0.5);
        this.applyPaleyAngle();
    }
    applyPaleyAngle() {
        if (this._params.boneType == BoneTypeEnum.Femur || this._params.boneType == BoneTypeEnum.Tibia) {
            const paleyData = Consts.AApaleyAngles[ViewType[this._params.viewType]][this._params.boneType][this._params.refType];
            let angle = MathUtils.degToRad(this._params.axisType == AnatomicalAxisType.reference ? paleyData.ref : paleyData.mov) *
                (this._params.axisType == AnatomicalAxisType.reference ? 1 : -1) * (this._params.refType === ReferenceTypeEnum.Proximal ? 1 : -1);
            angle = this._params.side == AnatomicalSideEnum.Left ? angle : Math.PI - angle;
            const AB = this.A.position.clone().sub(this.B.position).setLength(this.A1.position.distanceTo(this.A2.position) / 2);
            this.A1.position.addVectors(this.Ac.position, AB.clone().applyAxisAngle(Consts.planeNormal, Math.PI - angle));
            this.A2.position.addVectors(this.Ac.position, AB.clone().applyAxisAngle(Consts.planeNormal, -angle));
        }
    }
    getAngleLockCursor() {
        return `images/labels/${this._angleLocked ? "lock" : "unlock"}_icon.svg`;
    }
    bindEvents() {
        this.leftLabel.bindEvent('onDoubleClick', this.toggleLockAngle);
        this.rightLabel.bindEvent('onDoubleClick', this.toggleLockAngle);
        if (this._enableLimits) {
            this.bindEvent("onDragEnd", () => {
                for (const limitline of this._limitLines) {
                    limitline.clearLimit();
                }
            });
        }
        this.center.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            const limitPoint = this.center.position.clone().sub(this.middlePointB1B2.position).projectOnVector(this.AB_dirOrigin).add(this.oppositePointLimit);
            const limits = [{ a: limitPoint, b: limitPoint.clone().add(this.AB_dirOrigin_perp), t: this.minDistance_Bc_oppositeBc + 1, v: this.A.position }];
            this._params.type === PlanTypeEnum.Fracture && limits.push({ a: this.oppositeBLimit, b: this.oppositeBLimit.clone().add(this.AB_dirOrigin_perp), t: this.minDistance_Bc_oppositeBc, v: this.A.position });
            LimitUtils.applyMultiLimits(args.position, this.center.position, limits, 5, this._limitLines);
            args.preventDefault = true;
            const translation = args.position.clone().sub(this.center.position);
            this.translateAllPoints(translation);
        });
        this.A1.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            if (this._angleLocked ? (this._params.type === PlanTypeEnum.Deformity ?
                this.computeLockedAngleA_Deformity(args.position, this.A2.position, this.A1.position, 1 - this.selectedConvexity) :
                this.computeLockedAngleA_Fracture(args.position, this.A2.position, this.A1.position, this.B1.position, this.B2.position, 1 - this.selectedConvexity, true)) :
                this.computeUnlockedAngleA(args.position, this.A1.position, this.A2.position, this.B1.position, this.B2.position, 1 - this.selectedConvexity)) {
                this.moveExternalPointA(args.position.clone().lerp(this.A2.position, this.selectedConvexity));
            }
            else {
                args.preventDefault = true;
            }
        });
        this.A2.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            if (this._angleLocked ? (this._params.type === PlanTypeEnum.Deformity ?
                this.computeLockedAngleA_Deformity(args.position, this.A1.position, this.A2.position, this.selectedConvexity) :
                this.computeLockedAngleA_Fracture(args.position, this.A1.position, this.A2.position, this.B2.position, this.B1.position, this.selectedConvexity, false)) :
                this.computeUnlockedAngleA(args.position, this.A2.position, this.A1.position, this.B2.position, this.B1.position, this.selectedConvexity)) {
                this.moveExternalPointA(this.A1.position.clone().lerp(args.position, this.selectedConvexity));
            }
            else {
                args.preventDefault = true;
            }
        });
        this.B1.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            if (this._angleLocked ? (this._params.type === PlanTypeEnum.Deformity ?
                this.computeLockedAngleB_Deformity(args.position, this.B2.position, this.B1.position, this.A1.position, this.A2.position, true) :
                this.computeLockedAngleB_Fracture(args.position, this.B1.position, this.B2.position, this.A1.position, this.A2.position)) :
                this.computeUnlockedAngleB(args.position, this.B1.position, this.B2.position, this.A1.position, this.A2.position)) {
                this.moveExternalPointB(args.position, this.B2);
            }
            else {
                args.preventDefault = true;
            }
        });
        this.B2.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            if (this._angleLocked ? (this._params.type === PlanTypeEnum.Deformity ?
                this.computeLockedAngleB_Deformity(args.position, this.B1.position, this.B2.position, this.A2.position, this.A1.position, false) :
                this.computeLockedAngleB_Fracture(args.position, this.B2.position, this.B1.position, this.A2.position, this.A1.position)) :
                this.computeUnlockedAngleB(args.position, this.B2.position, this.B1.position, this.A2.position, this.A1.position)) {
                this.moveExternalPointB(args.position, this.B1);
            }
            else {
                args.preventDefault = true;
            }
        });
        this.Ac.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            args.position.copy(VectorUtils.projectOnVector(args.position, this.Ac.position, this.middlePointB1B2.position));
            // does not allow to A1 A2 line to go under B1 B2 line
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(this.B1.position, this.B2.position, this.Ac.position, args.position, this.A1.position, this.A2.position, this.A.position);
            // does not allow to A1 A2 line to go under B1 B2 rotated line
            const dir = this.A1.position.clone().sub(this.A2.position);
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(this.B1.position, this.B1.position.clone().add(dir), this.Ac.position, args.position, this.A1.position, this.A2.position, this.A.position, this._limitLines[0], this._limitLines[1]);
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(this.B2.position, this.B2.position.clone().add(dir), this.Ac.position, args.position, this.A1.position, this.A2.position, this.A.position);
            // prevent Ac to go under Bc plus a minimum distance
            this.preventMinDistance(args.position, this.middlePointB1B2.position, this.minDistance_Ac_Bc);
            // update A when Ac move over A to take minimum distance between A and Ac
            const sign = VectorUtils.computeSign(args.position, this.A.position, this.Ac.position.clone().sub(this.A.position));
            const distance_A_Ac = args.position.distanceTo(this.A.position) * sign;
            if (distance_A_Ac < this.minDistance_A_Ac) {
                const dir = args.position.clone().sub(this.middlePointB1B2.position);
                this.A.position.copy(args.position.clone().add(dir.setLength(this.minDistance_A_Ac)));
            }
            const translation = args.position.clone().sub(this.Ac.position);
            this.A1.position.add(translation);
            this.A2.position.add(translation);
            this.center.position.lerpVectors(args.position, this.middlePointB1B2.position, 0.5);
        });
        this.Bc?.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            // project mouse on Ac Bc line
            args.position.copy(VectorUtils.projectOnVector(args.position, this.Ac.position, this.Bc.position));
            // does not allow to B1 B2 line to go under A1 A2 line
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(this.A1.position, this.A2.position, this.Bc.position, args.position, this.B1.position, this.B2.position, this.B.position);
            // does not allow to B1 B2 line to go under A1 A2 rotated line
            const dir = this.B1.position.clone().sub(this.B2.position);
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(this.A1.position, this.A1.position.clone().add(dir), this.Bc.position, args.position, this.B1.position, this.B2.position, this.B.position);
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(this.A2.position, this.A2.position.clone().add(dir), this.Bc.position, args.position, this.B1.position, this.B2.position, this.B.position);
            LimitUtils.applyLimitAndSetVector(this.oppositePointLimit, this.oppositePointLimit.clone().add(this.AB_dirOrigin_perp), args.position, this.Bc.position, this.Ac.position, this.minDistance_Bc_oppositeBc);
            // prevent Bc to go under Ac plus a minimum distance
            this.preventMinDistance(args.position, this.Ac.position, this.minDistance_Ac_Bc);
            // update B when Bc move over B to take minimum distance between B and Bc
            const translation = args.position.clone().sub(this.Bc.position);
            this.B1.position.add(translation);
            this.B2.position.add(translation);
            const sign = VectorUtils.computeSign(args.position, this.B.position, this.Bc.position.clone().sub(this.B.position));
            const distance_B_Bc = args.position.distanceTo(this.B.position) * sign;
            if (distance_B_Bc < this.minDistance_A_Ac) {
                const dir = args.position.clone().sub(this.Ac.position);
                this.B.position.copy(args.position.clone().add(dir.setLength(this.minDistance_A_Ac)));
            }
            // update center point
            this.center.position.lerpVectors(args.position, this.Ac.position, 0.5);
        });
        this.A.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            LimitUtils.applyMultiLimits(args.position, this.A.position, [
                { a: this.B.position, b: this.B.position.clone().add(this.AB_dirOrigin_perp), t: 0.01 }, // does not allow to A B line to exceed 90 degrees
            ], 5, this._limitLines);
            const newAngle = VectorUtils.linesAngle(this.B.position.clone().sub(this.AB_dirOrigin), args.position, this.B.position); //put in func
            const actualAngle = VectorUtils.linesAngle(this.B.position.clone().sub(this.AB_dirOrigin), this.A.position, this.B.position);
            // prevent Ac under opposite Ac
            const tolerance = this.AB_dirOrigin.clone().setLength(this.minDistance_Bc_oppositeBc);
            let diff;
            if (this._params.type === PlanTypeEnum.Deformity) {
                const newPoint = this.Bc.position.clone().sub(this.B.position).applyAxisAngle(Consts.planeNormal, newAngle - actualAngle).add(this.B.position).sub(tolerance);
                diff = newPoint.sub(this.oppositePointLimit).projectOnVector(this.AB_dirOrigin); //refactor
            }
            else {
                const newPoint = this.center.position.clone().sub(this.B.position).applyAxisAngle(Consts.planeNormal, newAngle - actualAngle).add(this.B.position).sub(tolerance);
                diff = newPoint.sub(this.oppositeBLimit).projectOnVector(this.AB_dirOrigin); //refactor
            }
            if (VectorUtils.haveSameDirection(this.AB_dirOrigin, diff)) {
                this.rotationPoints(newAngle - actualAngle, this.B.position);
            }
            else {
                args.position.copy(VectorUtils.projectOnVector(args.position, this.A.position, this.B.position));
                const limitPoint = this.Ac.position.clone().add(this.AB.setLength(this.minDistance_A_Ac)); //frattura
                if (VectorUtils.haveSameDirection(this.AB, limitPoint.clone().sub(args.position))) {
                    args.position.copy(limitPoint);
                }
            }
            // if A is below Ac, move A to keep minimum distance
            const AToB = args.position.clone().sub(this.B.position);
            const minDistance = AToB.clone().setLength(this.minDistance_A_Ac);
            const AToAc = args.position.clone().sub(this.Ac.position).sub(minDistance);
            if (!VectorUtils.haveSameDirection(AToB, AToAc)) {
                args.position.addVectors(this.Ac.position, AToB.setLength(this.minDistance_A_Ac));
            }
        });
        this.B.bindEvent('onDragMove', (args) => {
            if (bindedModelBase.syncAnatomicalAxis)
                return;
            if (this._params.type === PlanTypeEnum.Deformity) {
                LimitUtils.applyMultiLimits(args.position, this.B.position, [
                    { a: this.A.position, b: this.A.position.clone().add(this.AB_dirOrigin_perp), t: 0.01 }, // does not allow to A B line to exceed 90 degrees
                    { a: this.B1.position, b: this.B2.position, t: this.minDistance_A_Ac } // prevent B to go under Bc plus a minimum distance
                ], 5, this._limitLines);
                const newAngle = VectorUtils.linesAngle(this.A.position.clone().sub(this.AB_dirOrigin), args.position, this.A.position); //put in func
                const actualAngle = VectorUtils.linesAngle(this.A.position.clone().sub(this.AB_dirOrigin), this.B.position, this.A.position);
                // prevent Bc under opposite Bc
                const tolerance = this.AB_dirOrigin.clone().setLength(this.minDistance_Bc_oppositeBc);
                const newBc = this.Bc.position.clone().sub(this.A.position).applyAxisAngle(Consts.planeNormal, newAngle - actualAngle).add(this.A.position).sub(tolerance);
                const diff = newBc.sub(this.oppositePointLimit).projectOnVector(this.AB_dirOrigin); //refactor
                if (VectorUtils.haveSameDirection(this.AB_dirOrigin, diff)) {
                    this.rotationPoints(newAngle - actualAngle, this.A.position);
                }
                else {
                    args.position.copy(VectorUtils.projectOnVector(args.position, this.A.position, this.B.position));
                    const limitPoint = this.Bc.position.clone().sub(this.AB.setLength(this.minDistance_A_Ac));
                    if (VectorUtils.haveSameDirection(this.AB, args.position.clone().sub(limitPoint))) {
                        args.position.copy(limitPoint);
                    }
                }
            }
            else {
                const limitPoint = this.B.position.clone().sub(this.center.position).projectOnVector(this.AB_dirOrigin).add(this.oppositeBLimit);
                LimitUtils.applyMultiLimits(args.position, this.B.position, [
                    { a: this.A.position, b: this.A.position.clone().add(this.AB_dirOrigin_perp), t: 1 }, // does not allow to A B line to exceed 90 degrees
                    { a: this.A1.position, b: this.A2.position, t: this.minDistance_A_Ac }, // prevent B to go under Ac plus a minimum distance
                    { a: this.oppositePointLimit, b: this.oppositePointLimit.clone().add(this.AB_dirOrigin_perp), t: this.minDistance_Bc_oppositeBc, v: this.A.position },
                    { a: limitPoint, b: limitPoint.clone().add(this.AB_dirOrigin_perp), t: this.minDistance_Bc_oppositeBc + 1, v: this.A.position }
                ], 5, this._limitLines);
                const newAngle = VectorUtils.linesAngle(this.A.position.clone().sub(this.AB_dirOrigin), args.position, this.A.position);
                const actualAngle = VectorUtils.linesAngle(this.A.position.clone().sub(this.AB_dirOrigin), this.B.position, this.A.position);
                this.rotationPoints(newAngle - actualAngle, this.A.position);
                const newB = this.B.position.clone().sub(this.A.position).applyAxisAngle(Consts.planeNormal, newAngle - actualAngle).add(this.A.position);
                const dirBSegment = this.B1.position.clone().sub(this.B2.position).multiplyScalar(0.5);
                const dirAB = newB.clone().sub(this.A.position);
                const newB1 = args.position.clone().add(dirBSegment);
                const newB2 = args.position.clone().sub(dirBSegment);
                let temp = newB1.clone();
                let excess = LimitUtils.applyLimitAndSetVector2(this.A1.position, this.A2.position, newB1, dirAB, newB, 5) ? newB1.sub(temp) : Consts.emptyVector3;
                temp = newB2.clone();
                const excess2 = LimitUtils.applyLimitAndSetVector2(this.A1.position, this.A2.position, newB2, dirAB, newB, 5) ? newB2.sub(temp) : Consts.emptyVector3;
                excess = excess2.length() > excess.length() ? excess2 : excess;
                args.position.add(excess);
                this.B1.position.addVectors(args.position, dirBSegment);
                this.B2.position.subVectors(args.position, dirBSegment);
                this.center.position.lerpVectors(this.Ac.position, args.position, 0.5);
            }
        });
    }
    preventMinDistance(position, point, minDistance) {
        const distance = position.distanceTo(point);
        if (distance < minDistance) {
            const dir = position.clone().sub(point);
            position.copy(point.clone().add(dir.setLength(minDistance)));
        }
    }
    moveExternalPointA(newAc) {
        const distance_A_Ac = this.Ac.position.distanceTo(this.A.position);
        const distance_B_Bc = this.middlePointB1B2.position.distanceTo(this.B.position);
        this.Ac.position.copy(newAc);
        const dir = this.Ac.position.clone().sub(this.middlePointB1B2.position);
        this.A.position.copy(this.Ac.position.clone().add(dir.setLength(distance_A_Ac)));
        if (this._params.type === PlanTypeEnum.Deformity) {
            this.B.position.copy(this.Bc.position.clone().sub(dir.setLength(distance_B_Bc)));
        }
        this.center.position.lerpVectors(this.Ac.position, this.middlePointB1B2.position, 0.5);
    }
    moveExternalPointB(position, oppositePoint) {
        const distance_A_Ac = this.Ac.position.distanceTo(this.A.position);
        const distance_B_Bc = this.middlePointB1B2.position.distanceTo(this.B.position);
        this.middlePointB1B2.position.lerpVectors(position, oppositePoint.position, 0.5);
        const dir = this.middlePointB1B2.position.clone().sub(this.Ac.position);
        if (this._params.type === PlanTypeEnum.Deformity) {
            this.B.position.copy(this.Bc.position.clone().add(dir.setLength(distance_B_Bc)));
        }
        this.A.position.copy(this.Ac.position.clone().sub(dir.setLength(distance_A_Ac)));
        this.center.position.lerpVectors(this.Ac.position, this.middlePointB1B2.position, 0.5);
    }
    rotationPoints(angle, rotationCenter) {
        const matrix = new Matrix4();
        matrix.makeRotationZ(angle);
        this.A1.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.A2.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.B1.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.B2.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.Ac.position.lerpVectors(this.A1.position, this.A2.position, this.selectedConvexity);
        this.Bc?.position.lerpVectors(this.B1.position, this.B2.position, 0.5);
        this.center.position.lerpVectors(this.Ac.position, this.middlePointB1B2.position, 0.5);
    }
    translateAllPoints(translation) {
        this.A.position.add(translation);
        this.B.position.add(translation);
        this.Ac.position.add(translation);
        this.Bc?.position.add(translation);
        this.A1.position.add(translation);
        this.A2.position.add(translation);
        this.B1.position.add(translation);
        this.B2.position.add(translation);
        this.center.position.add(translation);
    }
    rotateAllPoints(angle, rotationCenter) {
        const matrix = new Matrix4();
        matrix.makeRotationZ(angle);
        this.A1.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.A2.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.B1.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.B2.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.Ac.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.Bc?.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.center.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.A.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
        this.B.position.sub(rotationCenter).applyMatrix4(matrix).add(rotationCenter);
    }
    reset() {
        this.initPosition(this._params.angle, this._params.translHor, this._params.translVer);
        this.selectedConvexity = this._params.convexityList.DefaultConvexity;
        !this._angleLocked && this.toggleLockAngle(undefined, false);
    }
    computeUnlockedAngleA(position, draggingPoint, oppositePoint, draggingPointB, oppositePointB, convexity) {
        const int = VectorUtils.lines2DIntersection(oppositePoint, oppositePoint.clone().add(this.AB_dirOrigin), this.middlePointB1B2.position, this.middlePointB1B2.position.clone().add(this.AB_dirOrigin_perp));
        const preventReversePoint = this.middlePointB1B2.position.clone().add(int.clone().sub(oppositePoint).divideScalar(convexity / (1 - convexity)));
        LimitUtils.applyMultiLimits(position, draggingPoint, [
            { a: oppositePoint, b: this.middlePointB1B2.position },
            { a: draggingPointB, b: oppositePointB, v: this.A.position },
            { a: oppositePoint, b: draggingPointB, v: this.A.position },
            { a: preventReversePoint, b: preventReversePoint.clone().add(this.AB_dirOrigin_perp), t: 1 }, // does not allow to Ac to swith with Bc (does not allow to A B line to exceed 90 degrees)
        ], 5, this._limitLines);
        return true;
    }
    computeUnlockedAngleB(position, draggingPoint, oppositePoint, draggingPointA, oppositePointA) {
        const int = VectorUtils.lines2DIntersection(oppositePoint, oppositePoint.clone().add(this.AB_dirOrigin), this.Ac.position, this.Ac.position.clone().add(this.AB_dirOrigin_perp));
        const preventReversePoint = int.clone().add(int.clone().sub(oppositePoint));
        const intOpposite = VectorUtils.lines2DIntersection(oppositePoint, oppositePoint.clone().add(this.AB_dirOrigin), this.oppositePointLimit, this.oppositePointLimit.clone().add(this.AB_dirOrigin_perp));
        const preventReversePointOpposite = intOpposite.clone().add(intOpposite.clone().sub(oppositePoint));
        LimitUtils.applyMultiLimits(position, draggingPoint, [
            { a: this.A.position, b: this.B.position },
            { a: draggingPointA, b: oppositePointA, v: this.B.position },
            { a: draggingPointA, b: oppositePoint, v: this.B.position },
            { a: preventReversePoint, b: preventReversePoint.clone().add(this.AB_dirOrigin_perp), t: 1 }, // does not allow to Bc to swith with Ac (does not allow to A B line to exceed 90 degrees)
            { a: preventReversePointOpposite, b: preventReversePointOpposite.clone().add(this.AB_dirOrigin_perp), t: 1 } // does not allow to Bc to switch with opposite Bc
        ], 5, this._limitLines);
        return true;
    }
    computeLockedAngleA_Deformity(position, oppositePoint, draggingPoint, convexity) {
        LimitUtils.applyMultiLimits(position, draggingPoint, [
            { a: this.A.position, b: this.B.position, t: 5 / convexity },
            { a: this.B1.position, b: this.B2.position, v: this.A.position }
        ], 5, this._limitLines);
        const dir = oppositePoint.clone().sub(draggingPoint);
        let newAc = VectorUtils.lines2DIntersection(position, position.clone().add(dir), this.A.position, this.B.position);
        if (!newAc) {
            position.copy(draggingPoint);
            return false;
        }
        let distance = position.distanceTo(newAc);
        const newOppositePoint = newAc.clone().add(dir.setLength(distance * convexity / (1 - convexity)));
        if (LimitUtils.applyLimitAndSetVector2(this.B1.position, this.B2.position, newOppositePoint, this.A1.position.clone().sub(this.A2.position), this.A.position, 5)) {
            newAc = VectorUtils.lines2DIntersection(newOppositePoint, newOppositePoint.clone().add(dir), this.A.position, this.B.position);
            if (!newAc) {
                position.copy(draggingPoint);
                return false;
            }
            distance = newOppositePoint.distanceTo(newAc);
            const sign = VectorUtils.computeSign(newOppositePoint, this.Ac.position, VectorUtils.getPerpendicular(this.AB)); //ask fabio
            if (distance * sign < 40 * convexity) {
                position.copy(draggingPoint);
                return false;
            }
            position.subVectors(newAc, dir.setLength(distance * (1 - convexity) / convexity));
        }
        oppositePoint.copy(newOppositePoint);
        return true;
    }
    computeLockedAngleB_Deformity(position, oppositePoint, draggingPoint, draggingPointA, oppositePointA, isB1) {
        LimitUtils.applyMultiLimits(position, draggingPoint, [
            { a: this.A.position, b: this.B.position },
            { a: draggingPointA, b: oppositePointA, v: this.B.position },
            { a: draggingPointA, b: oppositePoint, v: this.B.position }
        ], 5, this._limitLines);
        const newPoints = this.calculateNewPoints(this.A1.position, this.A2.position, isB1 ? position : this.B1.position, isB1 ? this.B2.position : position);
        const newNormToAB = Consts.planeNormal.clone().cross(newPoints.AB);
        const projBOnAB = VectorUtils.lines2DIntersection(position, position.clone().add(newNormToAB), newPoints.A, newPoints.B);
        if (!projBOnAB)
            return false;
        const distanceAToProjAOnAB = projBOnAB.distanceTo(position);
        const oldAngle = Math.PI - draggingPoint.clone().sub(oppositePoint).angleTo(this.B.position.clone().sub(this.A.position));
        const newBcAngLock = projBOnAB.clone().sub(newPoints.AB.clone().setLength(distanceAToProjAOnAB / Math.tan(oldAngle)));
        const newOppositePoint = newBcAngLock.clone().add(newBcAngLock.clone().sub(position));
        if (VectorUtils.arePointsOnSameSide(this.A1.position, this.A2.position, Consts.planeNormal, this.B.position, newOppositePoint, position) && //todo add distanza minima
            VectorUtils.arePointsOnSameSide(this.oppositePointLimit, this.oppositePointLimit.clone().add(this.AB_dirOrigin_perp), Consts.planeNormal, this.A.position, newOppositePoint, position)) {
            oppositePoint.copy(newOppositePoint);
        }
        else {
            position.copy(draggingPoint);
        }
        const angle = VectorUtils.linesAngleFromOrigin(this.A.position.clone().sub(this.B.position), this.Ac.position.clone().sub(position.clone().lerp(oppositePoint, 0.5)));
        const matrix = new Matrix4().makeRotationZ(angle);
        this.A1.position.sub(this.Ac.position).applyMatrix4(matrix).add(this.Ac.position);
        this.A2.position.sub(this.Ac.position).applyMatrix4(matrix).add(this.Ac.position);
        return true;
    }
    computeLockedAngleA_Fracture(position, oppositePoint, draggingPoint, draggingPointB, oppositePointB, convexity, isA1) {
        const int = VectorUtils.lines2DIntersection(oppositePoint, oppositePoint.clone().add(this.AB_dirOrigin), this.middlePointB1B2.position, this.middlePointB1B2.position.clone().add(this.AB_dirOrigin_perp));
        const preventReversePoint = this.middlePointB1B2.position.clone().add(int.clone().sub(oppositePoint).divideScalar(convexity / (1 - convexity)));
        LimitUtils.applyMultiLimits(position, draggingPoint, [
            { a: this.A.position, b: this.B.position },
            { a: draggingPointB, b: oppositePointB, v: this.A.position },
            { a: oppositePoint, b: draggingPointB, v: this.A.position },
            { a: preventReversePoint, b: preventReversePoint.clone().add(this.AB_dirOrigin_perp), t: 1 }, // does not allow to Ac to swith with Bc (does not allow to A B line to exceed 90 degrees)
        ], 5, this._limitLines);
        const newPoints = this.calculateNewPoints(isA1 ? position : this.A1.position, isA1 ? this.A2.position : position, this.B1.position, this.B2.position);
        const newNormToAB = Consts.planeNormal.clone().cross(newPoints.AB);
        const projBOnAB = VectorUtils.lines2DIntersection(position, position.clone().add(newNormToAB), newPoints.A, newPoints.B);
        if (!projBOnAB)
            return false;
        const distanceAToProjAOnAB = projBOnAB.distanceTo(position);
        const oldAngle = draggingPoint.clone().sub(oppositePoint).angleTo(this.A.position.clone().sub(this.B.position));
        const newBcAngLock = projBOnAB.clone().sub(newPoints.AB.clone().setLength(distanceAToProjAOnAB / Math.tan(oldAngle)));
        const newOppositePoint = newBcAngLock.clone().add(newBcAngLock.clone().sub(position).multiplyScalar(convexity / (1 - convexity)));
        if (VectorUtils.arePointsOnSameSide(this.B1.position, this.B2.position, Consts.planeNormal, this.A.position, newOppositePoint, position)) { //todo add distanza minima
            oppositePoint.copy(newOppositePoint);
        }
        else {
            position.copy(draggingPoint);
        }
        return true;
    }
    computeLockedAngleB_Fracture(position, draggingPoint, oppositePoint, draggingPointA, oppositePointA) {
        LimitUtils.applyMultiLimits(position, draggingPoint, [
            { a: this.A.position, b: this.B.position },
            { a: draggingPointA, b: oppositePointA, v: this.B.position },
            { a: draggingPointA, b: oppositePoint, v: this.B.position }
        ], 5, this._limitLines);
        const newOppositePoint = this.B.position.clone().add(this.B.position.clone().sub(position));
        const newOppositePointLimited = newOppositePoint.clone();
        if (LimitUtils.applyLimitAndSetVector2(this.A1.position, this.A2.position, newOppositePointLimited, newOppositePoint.clone().sub(position), this.B.position, 5)) {
            const transl = newOppositePoint.sub(newOppositePointLimited);
            position.add(transl);
        }
        oppositePoint.copy(newOppositePointLimited);
        return true;
    }
    calculateNewPoints(A1, A2, B1, B2) {
        const points = {};
        points.Ac = A1.clone().lerp(A2, this.selectedConvexity);
        const Bc = B1.clone().lerp(B2, 0.5);
        const dA = this.A.position.distanceTo(this.Ac.position);
        const dirAB = points.Ac.clone().sub(Bc);
        points.A = points.Ac.clone().add(dirAB.setLength(dA));
        if (this._params.type === PlanTypeEnum.Deformity) {
            points.Bc = Bc;
            const dB = this.B.position.distanceTo(this.Bc.position);
            points.B = points.Bc.clone().add(dirAB.setLength(-dB));
        }
        else {
            points.B = Bc;
        }
        points.center = points.Ac.clone().lerp(Bc, 0.5);
        points.AB = points.A.clone().sub(points.B);
        return points;
    }
    cloneForEoC() {
        const obj = new AnatomicalAxis(this._params, true);
        obj.interceptedByRaycaster = false;
        obj.isEnabled = false;
        return obj;
    }
    copyPositionsTo(to) {
        to.A.position.copy(this.A.position);
        to.A1.position.copy(this.A1.position);
        to.A2.position.copy(this.A2.position);
        to.Ac.position.copy(this.Ac.position);
        to.B.position.copy(this.B.position);
        to.B1.position.copy(this.B1.position);
        to.B2.position.copy(this.B2.position);
        to.Bc?.position.copy(this.Bc.position);
        to.center.position.copy(this.center.position);
    }
}
__decorate([
    Save()
], AnatomicalAxis.prototype, "position", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "A", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "A1", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "A2", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "Ac", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "B", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "B1", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "B2", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "Bc", void 0);
__decorate([
    Save("position")
], AnatomicalAxis.prototype, "center", void 0);
__decorate([
    SaveChild()
], AnatomicalAxis.prototype, "leftLabel", void 0);
__decorate([
    SaveChild()
], AnatomicalAxis.prototype, "rightLabel", void 0);
__decorate([
    SaveChild()
], AnatomicalAxis.prototype, "nameLabel", void 0);
__decorate([
    Save()
], AnatomicalAxis.prototype, "_selectedConvexity", void 0);
//# sourceMappingURL=AnatomicalAxis.js.map